import React from 'react';
import { X, Info, Tent, Coffee, RotateCcw, Flame, ShieldCheck, Utensils, Caravan, Zap, AlertTriangle, Mountain, Smartphone, Layout, HelpCircle, Sparkles, Anchor, Construction, Droplet, Droplets, Waves, TreePine, Trees, UserCheck, Star, Landmark, Camera } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';

import { BRAND } from '../constants/mapColors';
interface SymbolLegendProps {
  onClose: () => void;
}

const SymbolLegend: React.FC<SymbolLegendProps> = ({ onClose }) => {
  const { t } = useTranslation();

  const LegendItem = ({ color, icon, title, desc }: { color: string, icon: React.ReactNode, title: string, desc: string }) => (
    <div className="flex items-start gap-5 p-4 bg-[var(--bg-surface-alt)] rounded-3xl border border-[var(--border-primary)] transition-all hover:bg-[var(--bg-surface)]">
      <div
        style={{ backgroundColor: color }}
        className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 shadow-lg border-2 border-[var(--border-secondary)] text-white"
      >
        {icon}
      </div>
      <div className="space-y-1">
        <h4 className="text-2xs font-black text-[var(--text-primary)] uppercase tracking-widest">{title}</h4>
        <p className="text-tiny text-[var(--text-secondary)] leading-relaxed">{desc}</p>
      </div>
    </div>
  );

  return (
    <div className="fixed left-0 right-0 bottom-0 z-[10001] flex items-center justify-center px-4 pb-4" style={{ top: 'env(safe-area-inset-top, 0px)' }}>
      <div className="absolute inset-0 bg-stone-950/90 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[var(--bg-base)] border border-[var(--border-primary)] rounded-t-modal md:rounded-modal shadow-3xl overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col max-h-[85dvh]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="p-8 border-b border-[var(--border-primary)] shrink-0 flex justify-between items-center bg-[var(--bg-input)]">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-emerald-500/10 rounded-2xl border border-emerald-500/20 text-emerald-500">
              <Info size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-black text-[var(--text-primary)] tracking-tighter">{t('info.title')}</h2>
              <p className="text-2xs text-[var(--text-muted)] font-bold uppercase tracking-widest">{t('info.subtitle')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors bg-[var(--bg-surface-alt)] rounded-xl">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-8 space-y-10 custom-scrollbar" style={{ overscrollBehavior: 'contain' }}>
          {/* TECHNOLOGY EXPLORATION SECTION */}
          <section className="space-y-4">
            <div className="bg-purple-500/5 border border-purple-500/20 p-6 rounded-panel space-y-4">
              <div className="flex items-center gap-3 text-purple-400">
                <Sparkles size={24} className="shrink-0" />
                <h3 className="text-sm font-black uppercase tracking-widest">{t('info.tech.title')}</h3>
              </div>
              <p className="text-[var(--text-secondary)] text-xs leading-relaxed font-medium">
                <Trans i18nKey="info.tech.desc" components={{ white: <strong className="text-[var(--text-primary)]" /> }} />
              </p>
            </div>
          </section>

          {/* DEVICE OPTIMIZATION SECTION */}
          <section className="space-y-4">
            <div className="bg-emerald-500/5 border border-emerald-500/20 p-6 rounded-panel space-y-4">
              <div className="flex items-center gap-3 text-emerald-400">
                <Layout size={24} className="shrink-0" />
                <h3 className="text-sm font-black uppercase tracking-widest">{t('info.optimization.title')}</h3>
              </div>
              <p className="text-[var(--text-secondary)] text-xs leading-relaxed font-medium">
                <Trans i18nKey="info.optimization.desc" components={{ white: <strong className="text-[var(--text-primary)]" /> }} />
              </p>
              <div className="flex flex-col gap-3">
                <div className="bg-[var(--bg-input)] p-4 rounded-2xl border border-[var(--border-primary)] flex items-start gap-3">
                  <Smartphone size={18} className="text-[var(--text-muted)] shrink-0 mt-0.5" />
                  <p className="text-2xs text-[var(--text-secondary)] leading-tight">
                    <Trans i18nKey="info.optimization.pwa_desc" components={{ white: <strong className="text-[var(--text-primary)]" /> }} />
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* AI DISCLAIMER & HELP */}
          <section className="space-y-4">
            <div className="bg-blue-500/10 border border-blue-500/30 p-6 rounded-panel space-y-4 shadow-2xl">
              <div className="flex items-center gap-3 text-blue-400">
                <AlertTriangle size={24} className="shrink-0" />
                <h3 className="text-sm font-black uppercase tracking-widest">{t('info.ai.title')}</h3>
              </div>
              <p className="text-[var(--text-secondary)] text-xs leading-relaxed font-medium">
                <Trans i18nKey="info.ai.desc" components={{ white: <strong className="text-[var(--text-primary)]" /> }} />
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <div className="bg-[var(--bg-input)] p-4 rounded-2xl border border-[var(--border-primary)] space-y-2">
                  <div className="flex items-center gap-2 text-amber-500 font-black text-3xs uppercase tracking-widest">
                    <ShieldCheck size={14} /> {t('info.ai.responsibility_title')}
                  </div>
                  <p className="text-2xs text-[var(--text-secondary)] leading-tight">
                    {t('info.ai.responsibility_desc')}
                  </p>
                </div>
                <div className="bg-[var(--bg-input)] p-4 rounded-2xl border border-[var(--border-primary)] space-y-2">
                  <div className="flex items-center gap-2 text-emerald-500 font-black text-3xs uppercase tracking-widest">
                    <HelpCircle size={14} /> {t('info.ai.feedback_title')}
                  </div>
                  <p className="text-2xs text-[var(--text-secondary)] leading-tight">
                    <Trans i18nKey="info.ai.feedback_desc" components={{ white: <strong className="text-[var(--text-primary)]" /> }} />
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Veg og Organisert */}
          <section className="space-y-4">
            <h3 className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-[0.2em] ml-2">{t('info.sections.road_organized')}</h3>
            <div className="grid gap-3">
              <LegendItem
                color="#1565C0"
                icon={<Caravan size={22} />}
                title={t('info.items.camping.title')}
                desc={t('info.items.camping.desc')}
              />
              <LegendItem
                color="#1565C0"
                icon={<Coffee size={20} />}
                title={t('info.items.rest_area.title')}
                desc={t('info.items.rest_area.desc')}
              />
              <LegendItem
                color="#1565C0"
                icon={<RotateCcw size={20} />}
                title={t('info.items.turning_point.title')}
                desc={t('info.items.turning_point.desc')}
              />
              <LegendItem
                color="#C62828"
                icon={<Construction size={20} />}
                title={t('info.items.barrier.title')}
                desc={t('info.items.barrier.desc')}
              />
            </div>
          </section>

          {/* Fricamping AI */}
          <section className="space-y-4">
            <h3 className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-[0.2em] ml-2">{t('info.sections.wildcamp_ai')}</h3>
            <div className="grid gap-3">
              <LegendItem
                color="#2E7D32"
                icon={<Tent size={20} />}
                title={t('info.items.wildcamp_good.title')}
                desc={t('info.items.wildcamp_good.desc')}
              />
              <LegendItem
                color="#f59e0b"
                icon={<Tent size={20} />}
                title={t('info.items.wildcamp_fine.title')}
                desc={t('info.items.wildcamp_fine.desc')}
              />
              <LegendItem
                color="#78716c"
                icon={<Tent size={20} />}
                title={t('info.items.wildcamp_uncertain.title')}
                desc={t('info.items.wildcamp_uncertain.desc')}
              />
            </div>
          </section>

          {/* Fasiliteter i terreng */}
          <section className="space-y-4">
            <h3 className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-[0.2em] ml-2">{t('info.sections.terrain_facilities')}</h3>
            <div className="grid gap-3">
              <LegendItem
                color={BRAND}
                icon={<Tent size={20} />}
                title={t('info.items.shelter.title')}
                desc={t('info.items.shelter.desc')}
              />
              <LegendItem
                color={BRAND}
                icon={<Mountain size={20} />}
                title={t('info.items.viewpoint.title')}
                desc={t('info.items.viewpoint.desc')}
              />
              <LegendItem
                color="#f97316"
                icon={<Flame size={20} />}
                title={t('info.items.firepit.title')}
                desc={t('info.items.firepit.desc')}
              />
              <LegendItem
                color="#3b82f6"
                icon={<Utensils size={20} />}
                title={t('info.items.picnic.title')}
                desc={t('info.items.picnic.desc')}
              />
              <LegendItem
                color="#0ea5e9"
                icon={<Waves size={20} />}
                title={t('info.items.swimming.title')}
                desc={t('info.items.swimming.desc')}
              />
            </div>
          </section>

          {/* Service & Infrastruktur */}
          <section className="space-y-4">
            <h3 className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-[0.2em] ml-2">{t('info.sections.service_infrastructure')}</h3>
            <div className="grid gap-3">
              <LegendItem
                color="#2563eb"
                icon={<Landmark size={20} />}
                title={t('info.items.toilet.title')}
                desc={t('info.items.toilet.desc')}
              />
              <LegendItem
                color="#3b82f6"
                icon={<Droplet size={20} />}
                title={t('info.items.drinking_water.title')}
                desc={t('info.items.drinking_water.desc')}
              />
              <LegendItem
                color="#8b5cf6"
                icon={<Droplets size={20} />}
                title={t('info.items.dump_station.title')}
                desc={t('info.items.dump_station.desc')}
              />
              <LegendItem
                color="#0288D1"
                icon={<Anchor size={20} />}
                title={t('info.items.quay.title')}
                desc={t('info.items.quay.desc')}
              />
              <LegendItem
                color="#F9A825"
                icon={<Zap size={20} />}
                title={t('info.items.charging.title')}
                desc={t('info.items.charging.desc')}
              />
            </div>
          </section>

          {/* Offentlige områder */}
          <section className="space-y-4">
            <h3 className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-[0.2em] ml-2">{t('info.sections.public_areas')}</h3>
            <div className="grid gap-3">
              <LegendItem
                color="#16a34a"
                icon={<TreePine size={20} />}
                title={t('info.items.state_recreation.title')}
                desc={t('info.items.state_recreation.desc')}
              />
              <LegendItem
                color="#15803d"
                icon={<Trees size={20} />}
                title={t('info.items.state_forest.title')}
                desc={t('info.items.state_forest.desc')}
              />
            </div>
          </section>

          {/* Overlegg-badges */}
          <section className="space-y-4">
            <h3 className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-[0.2em] ml-2">{t('info.sections.user_verification')}</h3>
            <p className="text-2xs text-[var(--text-secondary)] ml-2 -mt-2">{t('info.sections.badges_explanation', 'Disse vises som sm\u00e5 merker oppå kartpinnen:')}</p>
            <div className="grid gap-3">
              <div className="flex items-start gap-5 p-4 bg-[var(--bg-surface-alt)] rounded-3xl border border-[var(--border-primary)] transition-all hover:bg-[var(--bg-surface)]">
                <div className="relative w-12 h-12 shrink-0 flex items-center justify-center">
                  <div className="w-10 h-10 rounded-full bg-stone-500 border-2 border-white/60 flex items-center justify-center text-white shadow-lg">
                    <Tent size={16} />
                  </div>
                  <div className="absolute -bottom-1 -left-1 w-5 h-5 rounded-full bg-[#ec4899] border-2 border-white flex items-center justify-center shadow">
                    <UserCheck size={10} className="text-white" />
                  </div>
                </div>
                <div className="space-y-1">
                  <h4 className="text-2xs font-black text-[var(--text-primary)] uppercase tracking-widest">{t('info.items.user_contribution.title')}</h4>
                  <p className="text-tiny text-[var(--text-secondary)] leading-relaxed">{t('info.items.user_contribution.desc')}</p>
                </div>
              </div>
              <div className="flex items-start gap-5 p-4 bg-[var(--bg-surface-alt)] rounded-3xl border border-[var(--border-primary)] transition-all hover:bg-[var(--bg-surface)]">
                <div className="relative w-12 h-12 shrink-0 flex items-center justify-center">
                  <div className="w-10 h-10 rounded-full bg-stone-500 border-2 border-white/60 flex items-center justify-center text-white shadow-lg">
                    <Tent size={16} />
                  </div>
                  <div className="absolute -top-1 -left-1 w-5 h-5 rounded-full bg-[#fbbf24] border-2 border-white flex items-center justify-center shadow">
                    <Star size={10} className="text-white" />
                  </div>
                </div>
                <div className="space-y-1">
                  <h4 className="text-2xs font-black text-[var(--text-primary)] uppercase tracking-widest">{t('info.items.verified.title')}</h4>
                  <p className="text-tiny text-[var(--text-secondary)] leading-relaxed">{t('info.items.verified.desc')}</p>
                </div>
              </div>
              <div className="flex items-start gap-5 p-4 bg-[var(--bg-surface-alt)] rounded-3xl border border-[var(--border-primary)] transition-all hover:bg-[var(--bg-surface)]">
                <div className="relative w-12 h-12 shrink-0 flex items-center justify-center">
                  <div className="w-10 h-10 rounded-full bg-stone-500 border-2 border-white/60 flex items-center justify-center text-white shadow-lg">
                    <Tent size={16} />
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-brand border-2 border-white flex items-center justify-center shadow">
                    <Camera size={10} className="text-white" />
                  </div>
                </div>
                <div className="space-y-1">
                  <h4 className="text-2xs font-black text-[var(--text-primary)] uppercase tracking-widest">{t('info.items.has_photos.title', 'Har bilder')}</h4>
                  <p className="text-tiny text-[var(--text-secondary)] leading-relaxed">{t('info.items.has_photos.desc', 'Plassen har bilder — enten lastet opp av brukere eller automatisk hentet fra nærområdet.')}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Innmark / Utmark indikator */}
          <section className="space-y-4">
            <h3 className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-[0.2em] ml-2">{t('info.sections.land_type')}</h3>
            <p className="text-2xs text-[var(--text-secondary)] ml-2 -mt-2">{t('info.sections.land_type_explanation')}</p>
            <div className="grid gap-3">
              <div className="flex items-start gap-5 p-4 bg-[var(--bg-surface-alt)] rounded-3xl border border-[var(--border-primary)] transition-all hover:bg-[var(--bg-surface)]">
                <div className="relative w-12 h-12 shrink-0 flex items-center justify-center">
                  <div className="w-10 h-10 rounded-full bg-stone-500 flex items-center justify-center text-white shadow-lg" style={{ border: '2.5px solid #f59e0b' }}>
                    <Tent size={16} />
                  </div>
                </div>
                <div className="space-y-1">
                  <h4 className="text-2xs font-black text-[var(--text-primary)] uppercase tracking-widest">{t('info.items.innmark_border.title')}</h4>
                  <p className="text-tiny text-[var(--text-secondary)] leading-relaxed">{t('info.items.innmark_border.desc')}</p>
                </div>
              </div>
              <div className="flex items-start gap-5 p-4 bg-[var(--bg-surface-alt)] rounded-3xl border border-[var(--border-primary)] transition-all hover:bg-[var(--bg-surface)]">
                <div className="relative w-12 h-12 shrink-0 flex items-center justify-center">
                  <div className="w-10 h-10 rounded-full bg-stone-500 border-2 border-white/60 flex items-center justify-center text-white shadow-lg">
                    <Tent size={16} />
                  </div>
                </div>
                <div className="space-y-1">
                  <h4 className="text-2xs font-black text-[var(--text-primary)] uppercase tracking-widest">{t('info.items.utmark_border.title')}</h4>
                  <p className="text-tiny text-[var(--text-secondary)] leading-relaxed">{t('info.items.utmark_border.desc')}</p>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="p-6 border-t border-[var(--border-primary)] bg-[var(--bg-input)]">
          <button onClick={onClose} className="w-full bg-stone-100 text-stone-950 py-4 rounded-2xl font-black text-xs uppercase tracking-widest active:scale-95 transition-all">{t('info.button_understood')}</button>
        </div>
      </div>
    </div>
  );
};

export default SymbolLegend;
